<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :return-value.sync="dates"
    transition="scale-transition"
    offset-y
    bottom
    left
    min-width="auto"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        class="c-input-xs input-filter"
        :class="{ 'c-input-has-sort': hasSort }"
        v-model="dateFormatted"
        :label="label"
        :placeholder="placeholder"
        :prepend-inner-icon="sortPrependIcon"
        @click:prepend-inner="sortClick"
        :clearable="clearable"
        hide-details
        outlined
        dense
        readonly
        v-bind="attrs"
        v-on="on"
        @click:clear="clearDates"
      ></v-text-field>
    </template>

    <v-date-picker
      v-model="dates"
      range
      no-title
      scrollable
      :locale="$i18n.locale"
    >
      <div class="d-block w-100">
        <div
          class="error--text text-center font-italic fs-14px"
          v-if="maxRange && maxRange > 0"
        >
          Chọn tối đa {{ maxRange }} ngày
        </div>
        <div class="my-2">
          <v-row no-gutters>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="primary"
                :outlined="isThisWeek()"
                :text="!isThisWeek()"
                @click="setThisWeek"
              >
                {{ $t("Tuần này") }}
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="primary"
                :outlined="isThisMonth()"
                :text="!isThisMonth()"
                @click="setThisMonth"
              >
                {{ $t("Tháng này") }}
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="primary"
                :outlined="isLastMonth()"
                :text="!isLastMonth()"
                @click="setLastMonth"
              >
                {{ $t("Tháng Trc") }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <div class="my-2">
          <v-row no-gutters>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="purple"
                :outlined="isLastDay(7)"
                :text="!isLastDay(7)"
                @click="setLastDay(7)"
              >
                7 Ngày
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="purple"
                :outlined="isLastDay(15)"
                :text="!isLastDay(15)"
                @click="setLastDay(15)"
              >
                15 Ngày
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                x-small
                block
                color="purple"
                :outlined="isLastDay(30)"
                :text="!isLastDay(30)"
                @click="setLastDay(30)"
              >
                30 Ngày
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <div class="text-right">
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="menu = false">Cancel</v-btn>
          <v-btn text color="success" @click="saveDate">OK</v-btn>
        </div>
      </div>
    </v-date-picker>
  </v-menu>
</template>

<script>
import { debounce } from "lodash/function";
import moment from "moment";

export default {
  name: "DateRangeFilter",
  props: {
    hasSort: {
      type: Boolean,
      default: () => false,
    },
    name: {
      type: String,
      default: () => "",
    },
    sortName: {
      type: String,
      default: () => "",
    },
    sorting: {
      type: String,
      default: () => "",
    },
    label: {
      type: String,
      default: () => "",
    },
    placeholder: {
      type: String,
      default: () => "",
    },
    options: {
      type: Array,
      default: () => [],
    },
    defaultValue: {
      type: [Array, String],
      default: () => null,
    },
    allowNull: {
      type: Boolean,
      default: () => true,
    },
    clearable: {
      type: Boolean,
      default: () => true,
    },
    maxRange: {
      type: Number,
      default: () => 0,
    },
  },
  data: () => ({
    value: null,
    sortValue: null,
    dates: null,
    menu: null,
    dateFormatted: null,
  }),
  computed: {
    sortPrependIcon() {
      if (!this.hasSort) {
        return "";
      }
      switch (this.sortValue) {
        case "asc":
          return "mdi-arrow-up";
        case "desc":
          return "mdi-arrow-down";
        default:
          return "mdi-arrow-up-down";
      }
    },
  },
  watch: {
    dates() {
      this.dateFormatted =
        this.dates && this.dates[0] && this.dates[1]
          ? this.formatDateTime(this.dates[0], "DD/MM/YYYY") +
            " - " +
            this.formatDateTime(this.dates[1], "DD/MM/YYYY")
          : null;
    },
    sorting(val) {
      if (val !== this.sortName) {
        this.sortValue = null;
      }
    },
  },
  methods: {
    isThisWeek() {
      const startOfWeek = moment().startOf("week").format("YYYY-MM-DD");
      const endOfWeek = moment().endOf("week").format("YYYY-MM-DD");
      return (
        this.dates &&
        this.dates[0] === startOfWeek &&
        this.dates[1] === endOfWeek
      );
    },
    isThisMonth() {
      const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
      const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
      return (
        this.dates &&
        this.dates[0] === startOfMonth &&
        this.dates[1] === endOfMonth
      );
    },
    isLastMonth() {
      const startOfLastMonth = moment()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      const endOfLastMonth = moment()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
      return (
        this.dates &&
        this.dates[0] === startOfLastMonth &&
        this.dates[1] === endOfLastMonth
      );
    },
    isLastDay(day) {
      const startOfDay = moment().format("YYYY-MM-DD");
      const endOfDay = moment()
        .subtract(day - 1, "days")
        .format("YYYY-MM-DD");
      return (
        this.dates && this.dates[0] === startOfDay && this.dates[1] === endOfDay
      );
    },
    setLastDay(day) {
      const startOfDay = moment().format("YYYY-MM-DD");
      const endOfDay = moment()
        .subtract(day - 1, "days")
        .format("YYYY-MM-DD");
      this.dates = [startOfDay, endOfDay];
      this.saveDate();
    },
    setThisWeek() {
      const startOfWeek = moment().startOf("week").format("YYYY-MM-DD");
      const endOfWeek = moment().endOf("week").format("YYYY-MM-DD");
      this.dates = [startOfWeek, endOfWeek];
      this.saveDate();
    },
    setThisMonth() {
      const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
      const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
      this.dates = [startOfMonth, endOfMonth];
      this.saveDate();
    },
    setLastMonth() {
      const startOfLastMonth = moment()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      const endOfLastMonth = moment()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
      this.dates = [startOfLastMonth, endOfLastMonth];
      this.saveDate();
    },
    onFilter: debounce(function () {
      const dates = this.dates ? [...this.dates] : null;
      if (dates && dates[0] && dates[1]) {
        const date0 = dates[0];
        const date1 = dates[1];
        if (date0 > date1) {
          dates[0] = date1;
          dates[1] = date0;
        }
      }
      this.$emit("onFilter", {
        name: this.name,
        value: dates,
      });
    }, 500),
    saveDate() {
      if (this.dates && this.dates[0] && this.dates[1]) {
        const startDate = moment(this.dates[0]);
        const endDate = moment(this.dates[1]);
        const duration = endDate.diff(startDate, "days");

        if (this.maxRange && duration > this.maxRange) {
          this.$vToastify.error(
            this.$t("labels.select_max_day", { day: this.maxRange })
          );
          return false;
        }
      }

      this.$refs.menu.save(this.dates);
      this.onFilter();
    },
    clearDates() {
      if (!this.allowNull) {
        const dates = [
          moment().subtract("3", "months").format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ];
        this.dates = [...dates];
      } else {
        this.dates = null;
      }
      this.saveDate();
    },
    sortClick() {
      if (!this.sortValue) {
        this.sortValue = "asc";
      } else if (this.sortValue === "asc") {
        this.sortValue = "desc";
      } else if (this.sortValue === "desc") {
        this.sortValue = "asc";
      }
      this.$emit("onSort", {
        sort_by: this.sortName,
        sort_type: this.sortValue,
      });
    },
  },
  mounted() {
    if (this.defaultValue && this.defaultValue.length > 0) {
      this.dates = [...this.defaultValue];
      this.saveDate();
    }
  },
};
</script>
